import { User } from "../features/user";
import { hasApprovals } from "./feature-gates";

export const getUserFullName = (user: {
  fullName: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
}): string => {
  const { firstName, fullName, lastName } = user;

  if (fullName) {
    return fullName;
  }

  return `${firstName} ${lastName}`;
};

export const getUserFirstName = (user: {
  fullName: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
}): string => {
  const { firstName, fullName, lastName } = user;

  if (firstName) {
    return firstName;
  }

  return lastName || fullName || "";
};

export const getUserNameInitials = (user: {
  firstName: string | undefined;
  lastName: string | undefined;
}): string => {
  const fInit = user.firstName ? user.firstName[0] : "";
  const lInit = user.lastName ? user.lastName[0] : "";

  return fInit + lInit;
};

export const getUserDefaultStatus = (user: User) => {
  const postStatuses =
    user?.postStatuses && user?.postStatuses.length > 0
      ? user.postStatuses
      : [];
  const defaultPostStatus = postStatuses.find((status) => status.isDefault);

  return defaultPostStatus;
};

export const getUserDefaultStatusKey = (user: User) => {
  const defaultPostStatus = getUserDefaultStatus(user);

  return defaultPostStatus?.key;
};

/**
 * The default post-status key to stamp when creating or duplicating a post.
 *
 * Returns the user's configured default status key only when they hold the
 * approvals entitlement; otherwise null. Centralises the SKE-3711 gate so
 * every create/duplicate flow (calendar, bulk-create saga, PostActions,
 * PostActionsDropdown, FilterBar, StickyBulkActionsBar, approvals board)
 * stays consistent — a downgraded user never gets an approval status key
 * stamped on a freshly created/duplicated post.
 */
export const getCreateDefaultPostStatusKey = (
  user: User | null | undefined
): string | null =>
  user && hasApprovals(user) ? (getUserDefaultStatusKey(user) ?? null) : null;

export const isUserCollaborator = (user: User) => {
  return user.role === "collaborator";
};

/**
 * Whether the user is permitted to use the inbox.
 *
 * Collaborators are gated on `preferences.showInbox`; every other role always
 * has access. Use this to guard ANY inbox-only work — not just the inbox UI,
 * but background fetches and Pusher subscriptions too. A collaborator with
 * `showInbox: false` is rejected (403) by every private sked-inbox route, so
 * firing those calls for them is pointless.
 *
 * Centralizes the check previously inlined in `@inbox/app/app.tsx` and
 * `pages/home/components/modules/InboxStatistic.tsx`.
 */
export const hasInboxPermission = (user?: User | null): boolean =>
  user?.role === "collaborator" ? !!user?.preferences?.showInbox : true;

/**
 * Inverse of `hasApprovals` — true when the user does NOT currently have
 * approvals access.
 *
 * NAME NOTE: despite "upsell", this is now simply `!hasApprovals`, so it
 * returns true for ANY user without approvals — including a previously-entitled
 * user who DOWNGRADED (not only users who never had the feature). Callers (e.g.
 * `resolveDefaultAprovalStatus`) rely on this to drive the no-approvals UI
 * (default to a publishable status), which is correct for both cohorts.
 *
 * Previously this helper re-implemented its own blacklist check on
 * `post-status-version` (rejecting `"null" | "none"` as "no access"). That
 * is brittle: a new backend tier like `"premium"` would silently pass the
 * blacklist and grant access without matching the approved whitelist in
 * `feature-gates.hasApprovals`. Delegating keeps the two helpers in sync.
 */
export function isUpsellRequiredForApproval(user: User) {
  return !hasApprovals(user);
}

/** copied from public/components/app/services/user.service.js */
export function showMeridian(user: User) {
  return user.preferences?.timePreference === "12h";
}

export function userClockFormat(user: User): "h:mm a" | "HH:mm" {
  return showMeridian(user) ? "h:mm a" : "HH:mm";
}

export function userPostStatusList(user: User) {
  return user.postStatuses ? user.postStatuses || [] : [];
}

/**
 * Gets the list of post statuses for the user.
 * Replaces user.getPostStatusList() method that was attached by server.
 */
export function getPostStatusList(user: User | null | undefined) {
  if (!user) return [];
  return user.postStatuses || [];
}

/**
 * Returns true when the Approval Links button should be visible to this user.
 * Admins always see it. Collaborators see it unless the "Hide the external
 * review links window" permission has been enabled for them (hideReviewLink).
 */
export function canCrudExternalLinks(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.role === "admin") return true;
  return user.preferences?.hideReviewLink !== true;
}

/**
 * Gets the user's week start preference (0 = Sunday, 1 = Monday).
 * Replaces user.weekStart() method that was attached by server.
 */
export function getWeekStart(user: User | null | undefined): number {
  if (!user) return 0;
  return user.preferences?.weekStart ?? 0;
}

/**
 * Gets the moment.js format string for displaying short time based on user preference.
 * Replaces user.shortTimeMomentFormat() method that was attached by server.
 */
export function shortTimeMomentFormat(user: User | null | undefined): string {
  const is12Hour = user?.preferences?.timePreference === "12h";
  return is12Hour ? "ddd D MMM h:mm A" : "ddd D MMM HH:mm";
}
